/**
 * @fileoverview The gate in front of the editor, and NOTHING ELSE IN THIS APP.
 *
 * Reading a page needs no key, no account and no session, and that is the
 * product's central claim: an address in a URL renders a page for a stranger.
 * So this gate is mounted on the writing route only. Wrapping the app in it --
 * or in anything that holds first render while it asks a server who you are --
 * would put a sign-in screen in front of every shared link.
 *
 * THAT IS WHY THIS APP USES THE PLAIN AUTH PROVIDER AND NOT THE FLEET'S SHARED
 * ONE. `SharedAuthProvider` gives single sign-on across `*.cloistr.xyz`, and it
 * earns that by gating its children behind a signer round trip on every first
 * render. For an app where every screen needs a key that is exactly right. Here
 * the main screen is a public page, so the same gate would cost every reader a
 * pause and a sign-in message before content they are entitled to see without
 * an account at all. `useSharedSessionMaybe` exists precisely because bare
 * provider apps are a supported shape; this is one. The cost is that an author
 * already signed in on another Cloistr app signs in here too, once.
 *
 * AUTHENTICATION HAS THREE STATES, NOT TWO, and collapsing them is the known
 * root cause of the fleet's false-logout reports. "Not connected yet" is not
 * "logged out": a remote signer handshake takes seconds, and rendering a
 * sign-in screen during it teaches people that the app forgets them. Worse, a
 * handshake that STARTS and then FAILS -- an unreachable relay, an approval
 * that timed out -- is a signer problem and not an identity problem, so it gets
 * the recovery panel that offers "try again", never a credential prompt.
 */

import { useNostrAuth, type SignerInterface } from '@cloistr/auth';
import { LoginPrompt, SignerRecovery } from '@cloistr/ui/components';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { ContinueWithExtension } from './ContinueWithExtension';

/**
 * The sign-in copy, which NAMES WHAT THE PERSON WAS TRYING TO DO.
 *
 * Defaulted to the page wording rather than made required, so the two existing
 * callers are unchanged. It became worth parameterising when a third writing
 * route arrived: somebody who clicked "make a site" and is told "sign in to
 * write or edit a page" reasonably wonders whether they clicked the wrong thing.
 */
export function RequireSigner({
  children,
  title = 'Write a page',
  callToAction = 'Sign in to write or edit a page.',
}: {
  children: (signer: SignerInterface, pubkey: string) => ReactNode;
  title?: string;
  callToAction?: string;
}) {
  const { authState, signer } = useNostrAuth();

  const connecting = !!authState.isConnecting || !!authState.isSwitching;

  // A connection that began and then stopped without connecting. Tracked with a
  // ref rather than derived, because the only evidence that an attempt happened
  // at all is that `connecting` was true in an earlier render.
  const [connectFailed, setConnectFailed] = useState(false);
  const wasConnecting = useRef(false);

  useEffect(() => {
    if (connecting) {
      wasConnecting.current = true;
      setConnectFailed(false);
      return;
    }
    if (wasConnecting.current && !authState.isConnected) {
      wasConnecting.current = false;
      setConnectFailed(true);
    }
  }, [connecting, authState.isConnected]);

  if (authState.isConnected && signer && authState.pubkey) {
    return <>{children(signer, authState.pubkey)}</>;
  }

  if (connecting) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Connecting to your signer…
      </div>
    );
  }

  if (connectFailed) {
    return (
      <SignerRecovery
        error={{ code: 'CONNECTION_FAILED' }}
        onRetry={() => {
          setConnectFailed(false);
          window.location.reload();
        }}
        onGoBack={() => setConnectFailed(false)}
      />
    );
  }

  return (
    <>
      <ContinueWithExtension />
      <LoginPrompt
        title={title}
        subtitle="Pages are signed by you and published to relays you choose."
        callToAction={callToAction}
      />
    </>
  );
}
